<script lang="ts">
	import { format } from 'svelte-i18n';
	import { capFirstChar, mathNormalize, percent_from_value_range } from 'src/helpers/utilities';
	//Components
	import WeatherStationCard from './WeatherStationCard.svelte';
	import { celsius_to_fahrenheit, fahrenheit_to_celsius } from 'src/helpers/interface_helpers';

	export let temp: { value: string; rawValue: number; unit: 'C' | 'F' };
	export let feelsLike: string;
	export let minTemp24h: string;
	export let maxTemp24h: string;

	const tempRanges = [
		{
			from: -40,
			to: 0,
		},
		{
			from: 0,
			to: 20,
		},
		{
			from: 20,
			to: 40,
		},
		{
			from: 40,
			to: 60,
		},
	];

	const tempGradients = [
		{
			value: -40,
			color: '#00008B',
		},
		{
			value: -20,
			color: '#0000CD',
		},
		{
			value: -10,
			color: '#1E90FF',
		},
		{
			value: 0,
			color: '#00FFFF',
		},
		{
			value: 10,
			color: '#7FFF00',
		},
		{
			value: 20,
			color: '#FFFF00',
		},
		{
			value: 25,
			color: '#FFD580',
		},
		{
			value: 30,
			color: '#FFA500',
		},
		{
			value: 35,
			color: '#FF8C00',
		},
		{
			value: 40,
			color: '#FF0000',
		},
		{
			value: 60,
			color: '#B22222',
		},
	];

	const BAR_MIN = 2.5;
	const BAR_MAX = 105;

	const convertToFahrenheit = (value: number | null): string => {
		const unit = temp?.unit || 'C';
		if (!Number.isFinite(value)) {
			return `-- °${unit}`;
		}
		const tempValue = unit === 'F' ? celsius_to_fahrenheit(value) : value;
		return `${tempValue} °${unit}`;
	};

	let tempRange: { from: number; to: number };
	let gradients: { value: number; color: string }[];
	let leftCircleX: number = BAR_MIN;

	$: if (Number.isFinite(temp?.rawValue)) {
		const tempValue = temp?.unit === 'F' ? fahrenheit_to_celsius(temp?.rawValue) : temp?.rawValue;
		const value = mathNormalize(tempValue, tempRanges[0].from, tempRanges[tempRanges.length - 1].to);
		tempRange =
			tempRanges.find((range) => {
				return value >= range.from && value <= range.to;
			}) || tempRanges[0];
		gradients = tempGradients.filter((gradient) => {
			return tempRange.from <= gradient.value && tempRange.to >= gradient.value;
		});
		const p = percent_from_value_range(value, [tempRange.from, tempRange.to]);
		leftCircleX = BAR_MIN + (BAR_MAX - BAR_MIN) * (p / 100);
	} else {
		const tempRange = tempRanges[2];
		gradients = tempGradients.filter((gradient) => {
			return tempRange.from <= gradient.value && tempRange.to >= gradient.value;
		});
	}
</script>

<WeatherStationCard
	on:click
	icon="fa-solid fa-temperature-three-quarters"
	label={capFirstChar($format('title_chart_temperature', { default: 'temperature' }))}
	value={temp?.value}
	description={$format('title_feels_like', {
		default: 'Feels like',
	})}
	descriptionValue={feelsLike}
>
	<div slot="main" class="d-flex">
		<svg width="112" height="30" viewBox="0 0 112 30" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M107.691 2H4.3068C1.1981 2 0 4.0147 0 6.5C0 8.9853 1.1981 11 4.3068 11H107.691C110.8 11 112 9.1493 112 6.6641C112 4.1788 110.8 2 107.691 2Z"
				fill="url(#temp_gradient)"
			/>

			<circle
				cx="2.5"
				cy="6.5"
				r="5"
				fill="#EDEDED"
				stroke="#808589"
				stroke-width="1.2"
				transform={`translate(${leftCircleX}, 0)`}
			/>

			<text x="1" y="26" fill="#9A9FA4" font-size="9">{convertToFahrenheit(tempRange?.from)}</text>
			<text x="111" y="26" fill="#9A9FA4" font-size="9" text-anchor="end"
				>{convertToFahrenheit(tempRange?.to)}</text
			>

			<defs>
				<linearGradient
					id="temp_gradient"
					x1="0"
					y1="2"
					x2="112"
					y2="2"
					gradientUnits="userSpaceOnUse"
				>
					{#if gradients}
						{#each gradients as gradient, i}
							<stop offset={i / gradients.length} stop-color={gradient.color} />
						{/each}
					{/if}
				</linearGradient>
			</defs>
		</svg>
	</div>

	<div slot="info">
		<span class="mr-xs">
			<i class="fa-light fa-arrow-down mr-xxs"></i>{minTemp24h}
		</span>
		<span>
			<i class="fa-light fa-arrow-up mr-xxs"></i>{maxTemp24h}
		</span>
	</div>
</WeatherStationCard>

<style>
	svg {
		max-width: 100%;
		width: 140px;
		height: auto;
		margin-top: 25px;
	}
	svg circle {
		transition: transform 0.25s ease-in-out;
	}
	div > span {
		white-space: nowrap;
	}
</style>
